import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import useMissingFields, { FieldMessage } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import MultiFileAttachmentsPanel from "@/components/MultiFileAttachmentsPanel";
import { Button } from "@/components/ui/button";
import Ref from "@/components/Ref";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SaveCancelBar, { SCB_TEXT } from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";
import { useFormDraft, useTableDraft } from "@/lib/useDrafts";
import { confirmDiscard } from "@/lib/unsavedChanges";

/**
 * Batch B · Round B1 — Lead sheet dialog for file `{head}-02`.
 *
 * Rows = accounts mapped to the head. Columns computed at read time.
 * Auditor stores adjustments, ticks and WP refs per raw account, plus
 * head-level explanation, conclusion, preparer and reviewer. Agreement
 * to IFRS Statements is shown as a diff badge. Substantive-analytics
 * heads gain expectation / acceptable difference / SAP explanation.
 */
import Amount, { fmtNumber } from "@/components/Amount";
import SourceBadge from "@/components/SourceBadge";
import PriorSourceLabel from "@/components/PriorSourceLabel";   // B35-2
import LeadStatementLines from "@/components/LeadStatementLines";   // STEP 3 · rule 6 (B-6b)
import { asArray } from "@/lib/asArray";
import NoFurtherWorkPanel from "@/components/NoFurtherWorkPanel";   // Batch 34 · B34-8
import { RestatementLeadSheetBlock } from "@/components/restatement/RestatementViews";   // Prior-Period Restatement · Phase 3b
import { isBalanced } from "@/lib/tolerances";   // STEP 3 combined · B6c: the one "balanced" rule (mirror of tolerances.is_balanced)
const fmt = (n) => fmtNumber(n);
const MfCtx = React.createContext(null);   // Batch 27 · Add-on 1: the fields below mark themselves from the refusal

export default function LeadSheetDialog() {
  const [params, setParams] = useSearchParams();
  const code = params.get("openLead");
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const leadCols = useResizableColumns({ storageKey: "lead-sheet", defaults: [90, 240, 130, 130, 120, 130, 120, 80, 56, 120], mins: [70, 140, 100, 100, 96, 100, 96, 64, 48, 90] });   // account, name, PY final, CY unadj., adj., CY final, movement, %, tick, WP

  const load = useCallback(async () => {
    if (!code || !currentEngId) return;
    setLoading(true);
    try {
      const r = await api.get(`/engagements/${currentEngId}/head-folder-lead-sheets/${code}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally { setLoading(false); }
  }, [code, currentEngId, t]);

  useEffect(() => { load(); }, [load]);
  const mf = useMissingFields(lang);
  // Batch 28 · Follow-up 1 · F2: the account rows (prior year where typed, tick, WP ref) are row drafts with one Save / Cancel
  // bar under the table — Save sends only the changed accounts, one PATCH each (accounts_overrides of that account), a failed
  // account stays highlighted with its error; the head's own fields (explanation, SAP, no-prior-year reason, conclusion) are a
  // page-form draft with their own bar. Mark complete and Sign wait until both are saved; closing the dialog asks first.
  const td = useTableDraft({
    rows: data?.rows, getId: (r) => r.raw_account_code, reload: load,
    saveRow: (acct, p) => {
      const o = { ...p };
      if ("py_final" in o) { o.py_manual = parseFloat(o.py_final || 0); delete o.py_final; }
      return api.patch(`/engagements/${currentEngId}/head-folder-lead-sheets/${code}`, { accounts_overrides: { [acct]: o } });
    },
  });
  const fd = useFormDraft(data);
  const anyDirty = td.dirty || fd.dirty;

  if (!code) return null;
  const close = async () => {
    if (!(await confirmDiscard(anyDirty, lang))) return;
    const next = new URLSearchParams(params); next.delete("openLead");
    setParams(next, { replace: true });
  };

  const patch = async (body) => {
    try {
      const r = await api.patch(
        `/engagements/${currentEngId}/head-folder-lead-sheets/${code}`, body);
      setData(r.data);
      if (body.completed) mf.reset();
      return true;
    } catch (e) {
      if (!mf.apply(e)) toast.error(e?.response?.data?.detail || t("common.failed"));
      return false;
    }
  };
  const saveForm = async () => {
    if (!fd.dirty) return;
    const body = { ...fd.patch };
    ["sap_expectation_amount", "sap_acceptable_difference"].forEach((k) => { if (k in body) body[k] = parseFloat(body[k] || 0); });
    setSaving(true);
    try { if (await patch(body)) { fd.reset(); toast.success(SCB_TEXT[lang === "ar" ? "ar" : "en"].savedOk); } }
    finally { setSaving(false); }
  };
  const setDirty = (fn) => fd.setMany(fn({}));

  const draft = fd.value || {};
  const canComplete = data?.can_complete && !data?.completed;
  const isSap = data?.decision === "sap";
  const isAr = data?.decision === "ar";

  return (
    <MfCtx.Provider value={mf}>
    <FullScreenDialog open={true} onClose={close} testid="lead-sheet-dialog"
                      title={<span data-testid={`lead-sheet-title-${code}`}>
            <Ref>{code}-02</Ref> · {t("lead.title")} <SourceBadge source={data?.source} className="ms-2 align-middle" /> <PriorSourceLabel source={data?.prior_source} className="ms-1 align-middle" />
            {(data && (lang === "ar" ? (data.head_name_ar || data.head_name) : data.head_name)) && (
              <span className="ms-2 text-[12px] font-normal text-muted-foreground"
                    data-testid="lead-sheet-head-name">
                — {lang === "ar" ? (data.head_name_ar || data.head_name) : data.head_name}
              </span>
            )}
      </span>}>
        <div className="pe-1" data-testid="lead-sheet-scroll">
          {data && <SignOffBar itemType="lead_sheet" itemKey={code} refreshKey={data.updated_at} onChange={load} missing={mf.missing} saveFirst={anyDirty} />}
          {/* Round 11 · M3 — multi-file supporting attachments. */}
          <MultiFileAttachmentsPanel engId={currentEngId}
                                       contextType="lead_sheet"
                                       contextId={`${code}-02`}
                                       testid="lead-mfa" />
          {loading && (
            <div className="p-6 text-sm text-muted-foreground">{t("common.loading")}</div>
          )}
          {!loading && data && (
            <div className="space-y-3">
              <AgreementBanner data={data} />
              <RestatementLeadSheetBlock data={data} />   {/* Restatement Phase 3b (D-6) */}
              {!isAr && (
                <div className="overflow-x-auto">
                  <ResizableTable cols={leadCols} className="w-full text-[11px]" data-testid="lead-sheet-table">
                    <thead className="border-b bg-slate-50 dark:bg-slate-900/40">
                      <tr>
                        <ResizableTh index={0} className="px-2 py-1 text-start">{t("lead.col.account")}</ResizableTh>
                        <ResizableTh index={1} className="px-2 py-1 text-start">{t("lead.col.name")}</ResizableTh>
                        <ResizableTh index={2} className="px-2 py-1 text-end">
                          {t("lead.col.pyFinal")}
                          <div className="text-[8px] text-muted-foreground"
                               data-testid="lead-py-source">
                            {t(`lead.pySource.${data.py_source || "none"}`)}
                          </div>
                        </ResizableTh>
                        <ResizableTh index={3} className="px-2 py-1 text-end">{t("lead.col.cyUnadj")}
                          <div className="text-[8px] text-muted-foreground" data-testid="lead-layer-unadj">{t("lead.col.cyUnadjLayer")}</div></ResizableTh>
                        <ResizableTh index={4} className="px-2 py-1 text-end">{t("lead.col.adj")}
                          <div className="text-[8px] text-muted-foreground" data-testid="lead-layer-adj">{t("lead.col.adjLayer")}</div></ResizableTh>
                        <ResizableTh index={5} className="px-2 py-1 text-end">{t("lead.col.cyFinal")}
                          <div className="text-[8px] text-muted-foreground" data-testid="lead-layer-final">{t("lead.col.cyFinalLayer")}</div></ResizableTh>
                        <ResizableTh index={6} className="px-2 py-1 text-end">{t("lead.col.mvmt")}</ResizableTh>
                        <ResizableTh index={7} className="px-2 py-1 text-end">{t("lead.col.mvmtPct")}</ResizableTh>
                        <ResizableTh index={8} className="px-2 py-1 text-center">{t("lead.col.tick")}</ResizableTh>
                        <ResizableTh index={9} className="px-2 py-1 text-start">{t("lead.col.wp")}</ResizableTh>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {asArray(data.rows).map((r) => (
                        <LeadRow key={r.raw_account_code} row={td.rowValue(r)} td={td}
                                    disabled={!!data.completed}
                                    onPatch={(o) => Object.entries(o).forEach(([k, v]) => td.edit(r.raw_account_code, k, v))} />
                      ))}
                      <LeadStatementLines lines={data.statement_lines} lang={lang} />
                      <tr className="border-t-2 font-semibold" data-testid="lead-sheet-totals">
                        <td colSpan={2} className="px-2 py-1">{t("lead.totals")}</td>
                        <td className="px-2 py-1 text-end"><Amount value={data.totals.py_final} /></td>
                        <td className="px-2 py-1 text-end"><Amount value={data.totals.cy_unadjusted} /></td>
                        <td className="px-2 py-1 text-end"><Amount value={data.totals.adjustments} /></td>
                        <td className="px-2 py-1 text-end"><Amount value={data.totals.cy_final} /></td>
                        <td className="px-2 py-1 text-end"><Amount value={data.totals.movement} /></td>
                        <td colSpan={3} />
                      </tr>
                    </tbody>
                  </ResizableTable>
                  <div className="mt-1 text-[10px] text-muted-foreground"
                         data-testid="lead-sheet-tickmark-legend">
                    {t("lead.tickmarkLegend")}
                  </div>
                  <TableSaveBar draft={td} lang={lang} disabled={!!data.completed} testid="lead-table-save" />
                </div>
              )}

              {data.explanation_required && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-900 dark:bg-amber-950/20"
                     data-testid="lead-explanation-required">
                  {t("lead.explanationRequired")}
                </div>
              )}
              <TextField
                label={isAr ? t("lead.arMovementExpl") : t("lead.explanation")}
                value={draft.explanation || ""}
                onChange={(v) => setDirty((d) => ({ ...d, explanation: v }))}
                testid="lead-explanation" field="explanation"
                disabled={!!data.completed} />

              {isSap && (
                <SapBlock data={data} draft={draft} setDirty={setDirty} patch={patch} />
              )}

              {(data.py_source === "none") && (
                <TextField
                  label={t("lead.noPriorYearReason")}
                  value={draft.no_prior_year_reason || ""}
                  onChange={(v) => setDirty((d) => ({ ...d, no_prior_year_reason: v }))}
                  testid="lead-no-py-reason" field="no_prior_year_reason"
                  disabled={!!data.completed} />
              )}

              <TextField
                label={t("lead.conclusion")}
                value={draft.conclusion || ""}
                onChange={(v) => setDirty((d) => ({ ...d, conclusion: v }))}
                testid="lead-conclusion" field="conclusion"
                disabled={!!data.completed} />
              <NoFurtherWorkPanel engId={currentEngId} fsHead={code} />

              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                <div className="flex items-end justify-end gap-2">
                  {data.completed && (
                    <span className="rounded-md bg-emerald-100 px-2 py-1 text-[10px] text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                      <CheckCircle2 className="me-1 inline h-3 w-3" />
                      {t("hfWs.aging.completedBadge")}
                    </span>
                  )}
                  {/* Batch 27 · Add-on 1: clickable until completed — a refusal marks exactly what is missing */}
                  <Button size="sm" disabled={!!data.completed || anyDirty} variant={canComplete ? "default" : "outline"}
                          onClick={() => patch({ completed: true })}
                          data-testid="lead-mark-complete">
                    {t("lead.markComplete")}
                  </Button>
                </div>
              </div>
              <SaveCancelBar dirty={fd.dirty} saving={saving} onSave={saveForm} onCancel={fd.reset} lang={lang} disabled={!!data.completed} testid="lead-save-bar" />
            </div>
          )}
        </div>
    </FullScreenDialog>
    </MfCtx.Provider>
  );
}

function AgreementBanner({ data }) {
  const { t } = useI18n();
  if (data.fs_head_total == null) {
    return (
      <div className="rounded-md border bg-slate-50 p-2 text-xs dark:bg-slate-900/40"
             data-testid="lead-agree-banner-none">
        {t("lead.noFsTotal")}
      </div>
    );
  }
  const diff = data.difference_to_fs;
  const ok = isBalanced(diff);   // B6c: a missing difference is not "agrees" (fs_head_total == null is handled above)
  return (
    <div className={`rounded-md border p-2 text-xs ${ok
      ? "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20"
      : "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/20"}`}
         data-testid="lead-agree-banner">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold">{t("lead.agreesToFs")}</span>
        <span>{t("lead.leadTotal")}: <Amount value={data.totals.cy_final} /></span>
        <span>{t("lead.fsTotal")}: <Amount value={data.fs_head_total} /></span>
        <span>{t("lead.diffFs")}: <Amount value={diff} /></span>
      </div>
      {/* STEP 7b · F5: the prior year by the same rule — the FS's PY figure for the head and the lead sheet's difference to it */}
      <div className="mt-1 flex flex-wrap items-center gap-3" data-testid="lead-agree-banner-py">
        {data.fs_head_total_py == null ? <span className="text-muted-foreground">{t("s7b.lead.pyNotShown")}</span> : (<>
          <span>{t("s7b.lead.pyLeadTotal")}: <Amount value={data.totals?.py_final} /></span>
          <span>{t("s7b.lead.pyFsTotal")}: <Amount value={data.fs_head_total_py} /></span>
          <span className={isBalanced(data.difference_to_fs_py) ? "" : "font-semibold text-red-700"}
                data-testid="lead-agree-py-diff">{t("s7b.lead.pyDiff")}: {data.difference_to_fs_py == null ? "—" : <Amount value={data.difference_to_fs_py} />}</span>
        </>)}
      </div>
    </div>
  );
}

function LeadRow({ row, onPatch, disabled, td }) {
  const k = row.raw_account_code;
  return (
    <tr data-testid={`lead-row-${k}`} className={td.rowClass(k)}
        data-changed={td.isChanged(k) ? "1" : "0"} data-failed={td.errorOf(k) ? "1" : "0"}>
      <td className="px-2 py-1 font-mono"><Ref>{row.raw_account_code}</Ref><RowSaveError draft={td} id={k} testid={`lead-row-error-${k}`} /></td>
      <td className="px-2 py-1">{row.raw_account_name || "—"}</td>
      <td className="px-2 py-1 text-end">
        {(row.py_manual || row.py_source === "none") ? (
          <input type="number" value={row.py_final ?? 0}
                  onChange={(e) => onPatch({ py_final: e.target.value })}
                  disabled={disabled}
                  className="w-24 rounded border px-1 py-0.5 text-end font-mono dark:bg-slate-900"
                  data-testid={`lead-py-manual-${row.raw_account_code}`} />
        ) : (
          <Amount value={row.py_final} />
        )}
      </td>
      <td className="px-2 py-1 text-end"><Amount value={row.cy_unadjusted} /></td>
      <td className="px-2 py-1 text-end">
        {/* Batch 13 · AE4: the column holds the POSTED adjusting entries (one source); the entries behind it on hover / click */}
        <AdjCell row={row} />
      </td>
      <td className="px-2 py-1 text-end"><Amount value={row.cy_final} /></td>
      <td className="px-2 py-1 text-end"><Amount value={row.movement} /></td>
      <td className="px-2 py-1 text-end font-mono">
        {row.movement_pct != null ? <Amount value={row.movement_pct} suffix="%" /> : "—"}
      </td>
      <td className="px-2 py-1 text-center">
        <input value={row.tick || ""} disabled={disabled} maxLength={4}
                onChange={(e) => onPatch({ tick: e.target.value })}
                className="w-12 rounded border px-1 py-0.5 text-center dark:bg-slate-900"
                data-testid={`lead-tick-${row.raw_account_code}`} />
      </td>
      <td className="px-2 py-1">
        <input value={row.wp_ref || ""} disabled={disabled}
                onChange={(e) => onPatch({ wp_ref: e.target.value })}
                className="w-24 rounded border px-1 py-0.5 dark:bg-slate-900"
                data-testid={`lead-wp-${row.raw_account_code}`} />
      </td>
    </tr>
  );
}

function SapBlock({ data, draft, setDirty, patch }) {
  const { t } = useI18n();
  const sap = data.sap || {};
  const disabled = !!data.completed;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/50 p-2 dark:border-amber-900 dark:bg-amber-950/20"
         data-testid="lead-sap-block">
      <div className="mb-1 text-xs font-semibold">{t("lead.sap.title")}</div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <SmallField label={t("lead.sap.expectationAmount")} value={draft.sap_expectation_amount ?? ""}
                      onChange={(v) => setDirty((d) => ({ ...d, sap_expectation_amount: v }))}
                      testid="lead-sap-exp-amt" field="sap_expectation_amount" type="number" disabled={disabled} />
        <SmallField label={t("lead.sap.acceptableDifference")} value={draft.sap_acceptable_difference ?? ""}
                      onChange={(v) => setDirty((d) => ({ ...d, sap_acceptable_difference: v }))}
                      testid="lead-sap-acc-diff" field="sap_acceptable_difference" type="number" disabled={disabled} />
      </div>
      <TextField label={t("lead.sap.expectationBasis")}
                 value={draft.sap_expectation_basis || ""}
                 onChange={(v) => setDirty((d) => ({ ...d, sap_expectation_basis: v }))}
                 testid="lead-sap-basis" disabled={disabled} />
      <div className="mt-1 text-[11px]" data-testid="lead-sap-diff">
        {t("lead.sap.difference")}: <span className="font-mono">{sap.difference == null ? "—" : fmt(sap.difference)}</span>
        {sap.over_threshold && (
          <span className="ms-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-800 dark:bg-red-950/40 dark:text-red-300"
                data-testid="lead-sap-over">
            <AlertTriangle className="me-0.5 inline h-2.5 w-2.5" />
            {t("lead.sap.overThreshold")}
          </span>
        )}
      </div>
      {sap.over_threshold && (
        <TextField label={t("lead.sap.explanation")}
                   value={draft.sap_explanation || ""}
                   onChange={(v) => setDirty((d) => ({ ...d, sap_explanation: v }))}
                   testid="lead-sap-expl" field="sap_explanation" disabled={disabled} />
      )}
    </div>
  );
}

function TextField({ label, value, onChange, onBlur, testid, disabled, field }) {
  const mf = React.useContext(MfCtx);
  const cls = "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900";
  return (
    <div>
      <label className="block text-[10px] uppercase text-muted-foreground">{label}</label>
      <textarea value={value} disabled={disabled}
                  onChange={(e) => { onChange(e.target.value); if (mf && field) mf.filled(field, e.target.value); }} onBlur={onBlur}
                  {...(mf && field ? mf.props(field, cls) : { className: cls })} rows={2}
                  data-testid={testid} />
      {field && <FieldMessage mf={mf} field={field} testid={`${testid}-missing`} />}
    </div>
  );
}
function SmallField({ label, value, onChange, onBlur, testid, disabled, type, field }) {
  const mf = React.useContext(MfCtx);
  const cls = "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900";
  return (
    <div>
      <label className="block text-[10px] uppercase text-muted-foreground">{label}</label>
      <input type={type || "text"} value={value ?? ""} disabled={disabled}
              onChange={(e) => { onChange(e.target.value); if (mf && field) mf.filled(field, e.target.value); }} onBlur={onBlur}
              {...(mf && field ? mf.props(field, cls) : { className: cls })}
              data-testid={testid} />
      {field && <FieldMessage mf={mf} field={field} testid={`${testid}-missing`} />}
    </div>
  );
}


/* Batch 13 · AE4 — the Adjustments cell: the amount (posted adjusting entries only) and, on click, the entries behind it.
   An amount typed into the old manual box (before Batch 13) is shown as information and is not counted. */
function AdjCell({ row }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const entries = asArray(row.adjustment_entries);
  return (
    <div className="inline-block text-end">
      <button type="button" className={`font-mono ${entries.length ? "underline decoration-dotted" : ""}`} onClick={() => entries.length && setOpen((o) => !o)}
              title={entries.length ? t("lead.col.adjEntries") : t("lead.col.noAdjEntries")} data-testid={`lead-adj-${row.raw_account_code}`}>
        {fmtNumber(row.adjustments || 0)}
      </button>
      {row.manual_adjustment_before_entries ? (
        <div className="text-[9px] text-amber-700" data-testid={`lead-adj-manual-${row.raw_account_code}`}>
          {t("lead.col.manualBefore")}{fmtNumber(row.manual_adjustment_before_entries)}
        </div>
      ) : null}
      {open && entries.length > 0 && (
        <div className="mt-1 rounded border bg-white p-1 text-start text-[10px] shadow dark:bg-slate-900" data-testid={`lead-adj-entries-${row.raw_account_code}`}>
          {entries.map((e, i) => (
            <div key={i} className="flex items-center gap-2 whitespace-nowrap">
              <a href="/fieldwork/adjusting-entries" className="font-mono text-sky-700 underline">{e.ref}</a>
              <span>{t(`aje.type.${e.type}`)}</span>
              <span className="font-mono">{e.debit ? `Dr ${fmtNumber(e.debit)}` : `Cr ${fmtNumber(e.credit)}`}</span>
              <span className="text-muted-foreground">{(e.description || "").slice(0, 40)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
